import { detectTimeZone } from "@so/shared";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider.jsx";
import { Alert, Button, Card, Field, Input } from "../components/ui.js";

type Mode = "sign-in" | "sign-up" | "confirm" | "new-password";

function messageOf(error: unknown): string {
  if (!(error instanceof Error)) return "Something went wrong. Please try again.";

  // Amplify's own wording is aimed at developers; these are the three a real
  // person actually hits.
  switch (error.name) {
    case "UserNotFoundException":
    case "NotAuthorizedException":
      return "That email address and password do not match.";
    case "UsernameExistsException":
      return "An account already exists for that email address. Try signing in.";
    case "CodeMismatchException":
      return "That confirmation code is not right. Check the email and try again.";
    case "ExpiredCodeException":
      return "That confirmation code has expired. Sign up again to get a new one.";
    case "InvalidPasswordException":
      return "That password does not meet the requirements below.";
    case "LimitExceededException":
      return "Too many attempts. Please wait a few minutes and try again.";
    default:
      return error.message;
  }
}

export function SignIn() {
  const auth = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>("sign-in");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const timezone = detectTimeZone();

  async function handle(event: FormEvent) {
    event.preventDefault();
    setError(undefined);
    setBusy(true);

    try {
      if (mode === "sign-in") {
        const result = await auth.signIn(email, password);

        // A staff account created by an administrator arrives with a one-time
        // password and cannot be used until it is replaced.
        if (result === "new-password-required") {
          setMode("new-password");
          return;
        }

        // Back to the root so the role-based landing applies. Without this a
        // new session inherits the last one's URL, which sends a coordinator
        // to a patient screen when roles are switched in one browser.
        navigate("/", { replace: true });
      } else if (mode === "new-password") {
        await auth.completeNewPassword(newPassword);
        navigate("/", { replace: true });
      } else if (mode === "sign-up") {
        const result = await auth.signUp({ email, password, name, timezone });
        if (result === "confirm-required") setMode("confirm");
        else await auth.signIn(email, password);
      } else {
        await auth.confirm(email, code);
        // Straight in rather than back to a login form - they have just typed
        // the password, asking for it again is friction for no benefit.
        await auth.signIn(email, password);
        navigate("/", { replace: true });
      }
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-5 py-12">
      <div className="mb-8 text-center">
        <svg viewBox="0 0 32 32" className="mx-auto mb-4 h-11 w-11" aria-hidden="true">
          <rect width="32" height="32" rx="7" className="fill-accent" />
          <path
            d="M9 16.5h4l2-5 2.5 9 2-4h3.5"
            stroke="white"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <h1 className="text-[24px] font-semibold tracking-[-0.01em]">
          {mode === "sign-up"
            ? "Create your account"
            : mode === "new-password"
              ? "Set your password"
              : "ReassureMed"}
        </h1>
        <p className="text-ink-soft mx-auto mt-2 max-w-sm text-[15px] leading-relaxed">
          {mode === "new-password"
            ? "Your account was created for you with a temporary password. Choose one only you know to finish setting it up."
            : mode === "confirm"
            ? `We sent a six-digit code to ${email}. Enter it below to finish setting up your account.`
            : mode === "sign-up"
              ? "An expert second opinion from specialists at accredited international hospitals."
              : "Sign in to your consultations."}
        </p>
      </div>

      <Card className="p-6">
        <form onSubmit={(e) => void handle(e)} className="space-y-4">
          {mode === "sign-up" && (
            <Field label="Your full name" required>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                required
                placeholder="Amara Osei"
              />
            </Field>
          )}

          <Field label="Email address" required>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              readOnly={mode === "confirm"}
              placeholder="you@example.com"
            />
          </Field>

          {mode === "new-password" && (
            <Field
              label="Choose a password"
              required
              hint="At least 10 characters, with an uppercase letter, a lowercase letter and a number."
            >
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
                minLength={10}
                autoFocus
              />
            </Field>
          )}

          {mode !== "confirm" && mode !== "new-password" && (
            <Field
              label="Password"
              required
              hint={
                mode === "sign-up"
                  ? "At least 10 characters, with an uppercase letter, a lowercase letter and a number."
                  : undefined
              }
            >
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === "sign-up" ? "new-password" : "current-password"}
                required
                minLength={10}
              />
            </Field>
          )}

          {mode === "confirm" && (
            <Field label="Confirmation code" required>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                placeholder="123456"
                className="font-mono tracking-[0.3em]"
              />
            </Field>
          )}

          {error !== undefined && <Alert tone="warn">{error}</Alert>}

          <Button type="submit" loading={busy} full>
            {mode === "sign-in"
              ? "Sign in"
              : mode === "sign-up"
                ? "Create account"
                : mode === "new-password"
                  ? "Set password and continue"
                  : "Confirm and continue"}
          </Button>
        </form>

        {mode !== "confirm" && mode !== "new-password" && (
          <p className="text-ink-soft mt-5 border-t border-line pt-4 text-center text-[14px]">
            {mode === "sign-in" ? "New here? " : "Already have an account? "}
            <button
              type="button"
              className="text-accent font-medium underline underline-offset-2"
              onClick={() => {
                setMode(mode === "sign-in" ? "sign-up" : "sign-in");
                setError(undefined);
              }}
            >
              {mode === "sign-in" ? "Create an account" : "Sign in"}
            </button>
          </p>
        )}
      </Card>

      {mode === "sign-up" && (
        <p className="text-ink-faint mt-5 text-center text-[12.5px] leading-relaxed">
          Times will be shown in your local timezone
          <span className="font-mono"> ({timezone})</span>. You can change this later.
        </p>
      )}

      <p className="text-ink-faint mt-6 text-center text-[12.5px]">
        Demonstration environment — do not enter real medical information.
      </p>
    </div>
  );
}
